import { ArrowLeft, CheckCircle2, Send } from "lucide-react";
import type React from "react";
import type { TestCase, Wallet } from "../types";
import { Button } from "./ui/button";
import { Card } from "./ui/card";

interface TestInstructionsViewProps {
	testCase: TestCase;
	wallet: Wallet;
	onSubmitResults: () => void;
	onBack: () => void;
}

export const TestInstructionsView: React.FC<TestInstructionsViewProps> = ({
	testCase,
	wallet: _wallet,
	onSubmitResults,
	onBack: _onBack,
}) => {
	const onBack = _onBack;
	// Simple markdown-like rendering
	const renderInstructions = (text: string) => {
		return text.split("\n").map((line, idx) => {
			// Bold text
			if (line.startsWith("**") && line.endsWith("**")) {
				const key = `bold-${line.substring(0, 20)}-${idx}`;
				return (
					<h4 key={key} className="mt-4 mb-2">
						{line.replace(/\*\*/g, "")}
					</h4>
				);
			}
			// Numbered list
			if (/^\d+\./.test(line)) {
				const key = `numbered-${line.substring(0, 20)}-${idx}`;
				return (
					<li key={key} className="mb-2 ml-5">
						{line.replace(/^\d+\.\s*/, "")}
					</li>
				);
			}
			// Bullet list
			if (line.trim().startsWith("-")) {
				const key = `bullet-${line.substring(0, 20)}-${idx}`;
				return (
					<li key={key} className="mb-1 ml-10 list-disc">
						{line.trim().replace(/^-\s*/, "")}
					</li>
				);
			}
			// Bold inline (amount = 1.00)
			const boldMatches = line.match(/\*\*(.*?)\*\*/g);
			if (boldMatches) {
				const parts = line.split(/\*\*(.*?)\*\*/);
				const key = `bold-inline-${line.substring(0, 20)}-${idx}`;
				return (
					<p key={key} className="mb-2">
						{parts.map((part, i) => {
							const partKey = `${key}-part-${i}`;
							return i % 2 === 1 ? (
								<strong key={partKey}>{part}</strong>
							) : (
								<span key={partKey}>{part}</span>
							);
						})}
					</p>
				);
			}
			// Empty line
			if (line.trim() === "") {
				// biome-ignore lint/suspicious/noArrayIndexKey: Empty lines don't have unique identifiers
				return <br key={`br-empty-${idx}`} />;
			}
			// Normal paragraph
			const key = `para-${line.substring(0, 20)}-${idx}`;
			return (
				<p key={key} className="mb-2">
					{line}
				</p>
			);
		});
	};

	return (
		<div>
			<Button type="button" variant="ghost" onClick={onBack} className="mb-4">
				<ArrowLeft size={18} />
				Back to Test Cases
			</Button>

			<Card className="border p-6">
				<h2 className="mt-0 mb-5 text-2xl">{testCase.name}</h2>

				<div className="mb-5 rounded-sm bg-(--color-muted) p-4">
					<h3 className="mt-0 mb-3 font-medium text-sm">Test Instructions</h3>
					<div className="text-(--color-foreground) text-sm leading-relaxed">
						{renderInstructions(testCase.instructions)}
					</div>
				</div>

				{/* What You'll Submit Section */}
				<div className="mb-5 rounded-sm bg-info-50 p-4">
					<h3 className="mt-0 mb-3 font-medium text-sm">What You'll Submit</h3>

					<div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-2">
						{testCase.requiredFields.referenceNumber && (
							<div className="flex items-center gap-2 rounded-xs bg-(--color-card) px-3 py-2 font-medium text-(--color-foreground) text-xs">
								<CheckCircle2 size={14} className="shrink-0 text-info-500" />
								<span>Reference Number</span>
							</div>
						)}
						{testCase.requiredFields.dateOfBirth && (
							<div className="flex items-center gap-2 rounded-xs bg-(--color-card) px-3 py-2 font-medium text-(--color-foreground) text-xs">
								<CheckCircle2 size={14} className="shrink-0 text-info-500" />
								<span>Date of Birth</span>
							</div>
						)}
						{testCase.requiredFields.transactionStatus && (
							<div className="flex items-center gap-2 rounded-xs bg-(--color-card) px-3 py-2 font-medium text-(--color-foreground) text-xs">
								<CheckCircle2 size={14} className="shrink-0 text-info-500" />
								<span>Transaction Status</span>
							</div>
						)}
						{testCase.requiredFields.amount && (
							<div className="flex items-center gap-2 rounded-xs bg-(--color-card) px-3 py-2 font-medium text-(--color-foreground) text-xs">
								<CheckCircle2 size={14} className="shrink-0 text-info-500" />
								<span>Amount</span>
							</div>
						)}
						{testCase.requiredFields.fees && (
							<div className="flex items-center gap-2 rounded-xs bg-(--color-card) px-3 py-2 font-medium text-(--color-foreground) text-xs">
								<CheckCircle2 size={14} className="shrink-0 text-info-500" />
								<span>Fees</span>
							</div>
						)}
						{testCase.requiredFields.amountReceived && (
							<div className="flex items-center gap-2 rounded-xs bg-(--color-card) px-3 py-2 font-medium text-(--color-foreground) text-xs">
								<CheckCircle2 size={14} className="shrink-0 text-info-500" />
								<span>Amount Received</span>
							</div>
						)}
						{testCase.requiredFields.transactionId && (
							<div className="flex items-center gap-2 rounded-xs bg-(--color-card) px-3 py-2 font-medium text-(--color-foreground) text-xs">
								<CheckCircle2 size={14} className="shrink-0 text-info-500" />
								<span>Transaction ID</span>
							</div>
						)}
						<div className="flex items-center gap-2 rounded-xs bg-(--color-card) px-3 py-2 font-medium text-(--color-foreground) text-xs">
							<CheckCircle2 size={14} className="shrink-0 text-info-500" />
							<span>Screenshots</span>
						</div>
					</div>
				</div>

				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={onBack}>
						Cancel
					</Button>
					<Button type="button" onClick={onSubmitResults}>
						<Send size={16} />
						Submit Test Results
					</Button>
				</div>
			</Card>
		</div>
	);
};
